import { AssetPaths } from '../assets/asset-store';
import { fetchAudioBytes } from './sound-buffer-cache';
import type { PreferenceStorage, SoundStore } from './sound-store';

export const MUSIC_CREDIT = 'This music was generously provided by s9meNINE';

interface Segment {
  readonly file: string;
  /** Shorter than the files, which carry 31ms/35ms of encoder padding — lengthen either and the seam ticks. */
  readonly seconds: number;
}

export interface MusicTrack {
  readonly intro: Segment;
  readonly loop: Segment;
}

export const MUSIC_TRACKS: Readonly<Record<number, MusicTrack>> = {
  1: {
    intro: { file: 'music/s9meNINE_1.ogg', seconds: 26.666 },
    loop: { file: 'music/s9meNINE_2.ogg', seconds: 213.333 },
  },
};

export interface MusicStoreOptions {
  readonly sounds: SoundStore;
  readonly fetchAudio?: ((url: string) => Promise<ArrayBuffer>) | undefined;
  readonly storage?: PreferenceStorage | null | undefined;
}

const SELECTION_KEY = 'musicTrack';

interface Playing {
  readonly track: number;
  readonly gain: GainNode;
  readonly sources: readonly AudioBufferSourceNode[];
}

/** Both nodes are scheduled in one block before either starts, so the intro-to-loop handover lands on a sample boundary. */
export class MusicStore {
  private readonly sounds: SoundStore;
  private readonly fetchAudio: (url: string) => Promise<ArrayBuffer>;
  private readonly storage: PreferenceStorage | null;
  private readonly buffers = new Map<string, AudioBuffer>();

  private selectionValue = 0;
  private playing: Playing | null = null;
  private loading = 0;

  started = 0;

  constructor(options: MusicStoreOptions) {
    this.sounds = options.sounds;
    this.fetchAudio = options.fetchAudio ?? fetchAudioBytes;
    this.storage = options.storage === undefined ? defaultStorage() : options.storage;
    this.selectionValue = readSelection(this.read());
    this.sounds.subscribe(() => {
      this.sync();
    });
  }

  get selection(): number {
    return this.selectionValue;
  }

  set selection(value: number) {
    const track = trackOrNone(value);
    if (track === this.selectionValue) return;
    this.selectionValue = track;
    this.write(String(track));
    this.sync();
  }

  get credit(): string {
    return this.selectionValue === 0 ? '' : MUSIC_CREDIT;
  }

  get playingTrack(): number {
    return this.playing?.track ?? 0;
  }

  private sync(): void {
    const wanted = this.sounds.enabled ? this.selectionValue : 0;
    if (this.playing !== null && this.playing.track !== wanted) this.stop();
    if (wanted === 0) return;
    if (this.playing !== null) {
      this.playing.gain.gain.value = this.sounds.musicGain;
      return;
    }

    const context = this.sounds.context;
    if (context === null) return;

    const track = MUSIC_TRACKS[wanted];
    if (track === undefined) return;

    const intro = this.buffers.get(track.intro.file);
    const loop = this.buffers.get(track.loop.file);
    if (intro === undefined || loop === undefined) {
      void this.load(context, track);
      return;
    }

    const gain = context.createGain();
    gain.gain.value = this.sounds.musicGain;
    gain.connect(context.destination);

    const at = context.currentTime + 0.05;
    const introSource = context.createBufferSource();
    introSource.buffer = intro;
    introSource.connect(gain);
    introSource.start(at, 0, track.intro.seconds);

    const loopSource = context.createBufferSource();
    loopSource.buffer = loop;
    loopSource.loop = true;
    loopSource.loopStart = 0;
    loopSource.loopEnd = track.loop.seconds;
    loopSource.connect(gain);
    loopSource.start(at + track.intro.seconds);

    this.playing = { track: wanted, gain, sources: [introSource, loopSource] };
    this.started += 1;
  }

  private stop(): void {
    const playing = this.playing;
    this.playing = null;
    if (playing === null) return;
    for (const source of playing.sources) {
      try {
        source.stop();
      } catch {
        // already stopped
      }
      source.disconnect();
    }
    playing.gain.disconnect();
  }

  private read(): string | null {
    try {
      return this.storage?.getItem(SELECTION_KEY) ?? null;
    } catch {
      return null;
    }
  }

  private write(value: string): void {
    try {
      this.storage?.setItem(SELECTION_KEY, value);
    } catch {
      // must not break the select the player just changed
    }
  }

  private async load(context: AudioContext, track: MusicTrack): Promise<void> {
    const token = (this.loading += 1);
    const files = [track.intro.file, track.loop.file].filter((f) => !this.buffers.has(f));
    try {
      const decoded = await Promise.all(
        files.map(async (file) => ({
          file,
          buffer: await context.decodeAudioData(await this.fetchAudio(AssetPaths.music(file))),
        })),
      );
      for (const { file, buffer } of decoded) this.buffers.set(file, buffer);
    } catch {
      return;
    }
    if (token === this.loading) this.sync();
  }
}

function defaultStorage(): PreferenceStorage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

function trackOrNone(value: number): number {
  return Number.isInteger(value) && value in MUSIC_TRACKS ? value : 0;
}

function readSelection(stored: string | null): number {
  if (stored === null || stored === '') return 0;
  return trackOrNone(Number(stored));
}
